import type { RecurlyError } from '@recurly/recurly-js';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js/dist/stripe-js/elements/express-checkout';
import type { StripeElementLocale, StripeElements, StripeElementsOptionsMode } from '@stripe/stripe-js/dist/stripe-js/elements-group';
import classNames from 'classnames';
import type { userAgent } from 'next/server';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { ApiError } from '@alltrails/core/utils/api';
import { getCopyrightText } from '@alltrails/core/utils/getCopyrightText';
import logError from '@alltrails/core/utils/logError';
import { executeRecaptcha } from '@alltrails/core/utils/recaptcha-helpers';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import TextInput from '@alltrails/denali/components/TextInput';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import {
  COLOR_TEXT_ERROR,
  COLOR_TEXT_TERTIARY,
  FONT_FAMILY_PRIMARY,
  FONT_LETTERSPACING_200,
  FONT_LINEHEIGHT_DEFAULT,
  FONT_SIZE_100,
  FONT_SIZE_200,
  FONT_WEIGHT_NORMAL
} from '@alltrails/denali/tokens';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { Plan } from '@alltrails/types/Plan';
import type PaymentTokenPayload from '../../types/PaymentTokenPayload';
import type ProResponse from '../../types/ProResponse';
import { CARD_VALIDATION_NAMES } from '../../types/RecurlyTokenization';
import getApplePayConfigForStripe from '../../utils/getApplePayConfigForStripe';
import getAvailablePlans from '../../utils/getAvailablePlans';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import { getPriceTotal, isTrial } from '../../utils/plan';
import toMinorUnits from '../../utils/toMinorUnits';
import BillingCountrySelector from '../BillingCountrySelector';
import ComplianceCheckbox from '../ComplianceCheckbox/ComplianceCheckbox';
import useTokenizedFormValidation from '../TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import tokenizedStyles from '../TokenizedCreditCardForm/TokenizedCreditCardForm.module.scss';
import { hasValidLastName, isValidPostalCode } from '../TokenizedCreditCardForm/utils';
import styles from './Checkout.module.scss';
import CouponCode from './CouponCode';
import ErrorText from './ErrorText';
import { payPalSupportedCurrencies } from './payPalSupportedCurrencies';
import { PaymentOptionValues } from './RadioPaymentForm/PaymentOptions';
import StripeApplePayButton from './StripeApplePayButton';
import StripeCheckoutModalFooter from './StripeCheckoutModalFooter';
import StripePayPalButton from './StripePayPalButton';
import TogglePaymentButton from './TogglePaymentButton/TogglePaymentButton';
import TrialReminder from './TrialReminder';

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY, {
  stripeAccount: process.env.NEXT_PUBLIC_STRIPE_ACCOUNT
});

const STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE: Partial<Record<string, PaymentTokenPayload['payment_method_type']>> = {
  apple_pay: 'apple_pay',
  card: 'card',
  google_pay: 'google_pay',
  [process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD]: 'paypal'
};

function getPaymentMethodTypeForToken(selectedPaymentMethod: string): PaymentTokenPayload['payment_method_type'] {
  const mapped = STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE[selectedPaymentMethod];
  if (mapped) return mapped;
  logError(new Error(`Unknown Stripe payment method: ${selectedPaymentMethod}`));
  return selectedPaymentMethod as PaymentTokenPayload['payment_method_type'];
}

function getAnalyticsPaymentMethod(selectedPaymentMethod: string): PaymentMethod {
  const mapped = STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE[selectedPaymentMethod];
  if (mapped) return mapped === 'card' ? 'credit_card' : mapped;
  logError(new Error(`Unknown Stripe payment method: ${selectedPaymentMethod}`));
  return selectedPaymentMethod as PaymentMethod;
}

// https://github.com/stripe/stripe-js/blob/master/types/stripe-js/elements/express-checkout.d.ts#L192
function getPaymentMethodTypeFromExpressCheckoutEvent(
  expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType']
): PaymentTokenPayload['payment_method_type'] {
  switch (expressPaymentType) {
    case 'apple_pay':
      return 'apple_pay';
    case 'google_pay':
      return 'google_pay';
    default:
      logError(new Error(`Unknown Stripe express checkout payment type: ${expressPaymentType}`));
      return expressPaymentType as PaymentTokenPayload['payment_method_type'];
  }
}

const LANGUAGE_REGION_TO_STRIPE_LOCALE: Record<LanguageRegionCode, StripeElementLocale> = {
  'da-DK': 'da',
  'de-DE': 'de',
  'en-GB': 'en-GB',
  'en-US': 'en',
  'es-ES': 'es-ES',
  'es-MX': 'es-419',
  'fr-FR': 'fr-FR',
  'it-IT': 'it-IT',
  'nb-NO': 'nb',
  'nl-NL': 'nl',
  'pl-PL': 'pl',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt',
  'sv-SE': 'sv'
};

type Props = {
  ariaLabelledBy?: string;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingClassName?: string;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  canUpgradeExistingSubscription: boolean;
  className?: string;
  coupon?: Coupon;
  couponError?: string;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  isComplianceCheckboxEnabled?: boolean;
  isMultiyearConversion?: boolean;
  isRadioUiEnabled?: boolean;
  isUpgrade?: boolean;
  onBillingCountryChange: (countryCode: CountryCode, response: unknown) => void;
  onSubmitTokenizedCardForm: (payload: { cc_token?: string; recaptcha: string }) => void;
  onPaymentToken: (payload: PaymentTokenPayload) => void | Promise<void>;
  plan: Plan;
  promoText?: ReactNode;
  purchaseTier: PurchaseTier;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  subscription?: SubscriptionsNewResponse['subscription'];
  webProPurchaseLocation: WebProPurchaseLocation;
  whenExpired?: string;
} & ({ isCheckoutModal: true; features?: never; trigger: UpsellTrigger } | { isCheckoutModal?: never; features: ReactNode; trigger?: never });

function StripeCheckoutBase({
  ariaLabelledBy,
  billedHeading,
  billedSubheading,
  billedSubheadingClassName,
  billedSubheadingRight,
  billedToday,
  billingCountry,
  buttonText,
  canUpgradeExistingSubscription,
  className,
  coupon,
  couponError,
  currencyCode,
  features,
  finePrint,
  heading,
  headingClassName,
  isCheckoutModal,
  isComplianceCheckboxEnabled = false,
  isMultiyearConversion,
  isRadioUiEnabled,
  isUpgrade,
  onBillingCountryChange,
  onSubmitTokenizedCardForm,
  onPaymentToken,
  plan,
  promoText,
  purchaseTier,
  setCoupon,
  setPlanToAnnual,
  subheading,
  subscription,
  trigger,
  webProPurchaseLocation,
  whenExpired
}: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const publicBaseUrl = usePublicBaseUrl();
  // `applePayConfig` is a dependency of the Stripe `useEffect` below; keep a stable object reference so we
  // do not tear down and recreate Elements on every unrelated re-render.
  const applePayConfig = useMemo(
    () =>
      webProPurchaseLocation !== 'gift page'
        ? {
            recurringPaymentRequest: getApplePayConfigForStripe(
              {
                canUpgradeExistingSubscription,
                coupon,
                isMultiyearConversion,
                plan,
                publicBaseUrl,
                purchaseTier,
                subscription,
                whenExpired
              },
              intl
            )
          }
        : undefined,
    [
      canUpgradeExistingSubscription,
      coupon,
      intl,
      isMultiyearConversion,
      plan,
      publicBaseUrl,
      purchaseTier,
      subscription,
      webProPurchaseLocation,
      whenExpired
    ]
  );
  const isMobile = useIsMobileSizedScreen();
  const searchParams = useClientNavigationSearchParams();
  // https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=1417-8321&t=aSZIfvtiFLXWHrXx-0
  const isIosNativeApp = isMobile && searchParams.get('app_source') === 'iOS';
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isChrome = browserName?.includes('Chrome');

  const { setClientValidationErrors, validationErrors } = useTokenizedFormValidation();
  const { nameError, postalCodeError } = validationErrors;

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<ProResponse['errors']>();
  const [fullName, setFullName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [isComplianceChecked, setIsComplianceChecked] = useState(false);
  const [complianceError, setComplianceError] = useState(false);

  const expressMountRef = useRef<HTMLDivElement>(null);
  const submitButtonContainerRef = useRef<HTMLDivElement>(null);
  const paymentMountRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const expressCheckoutElementRef = useRef<{ unmount: () => void } | null>(null);
  const paymentElementRef = useRef<{ unmount: () => void } | null>(null);
  const stripeRef = useRef<Awaited<ReturnType<typeof loadStripe>>>(null);

  const [selectedPaymentType, setSelectedPaymentType] = useState<string | undefined>(isIosNativeApp ? 'apple_pay' : 'card');

  const showBillingCountry = billingCountry && !isUpgrade;
  const showPostalCode = billingCountry === 'CA' || billingCountry === 'US';
  const isGiftCard = coupon?.type === 'GiftCard';

  const [submitButtonHeight, setSubmitButtonHeight] = useState(0);
  // If the iOS web checkout optimization is enabled, the submit button
  //  is fixed to the bottom of the page for all treatment variants.
  const [isButtonFixed, setIsButtonFixed] = useState(isIosNativeApp ? true : false);

  // Makes the submit button fixed to the bottom of the page until it's original position is scrolled to.
  // "position: sticky" isn't used since the button container needs to extend beyond its container.
  useLayoutEffect(() => {
    if (isIosNativeApp || !isMobile || !isComplianceCheckboxEnabled || isCheckoutModal) {
      return;
    }

    const el = submitButtonContainerRef.current;
    if (!el) {
      return;
    }

    const buttonHeight = el.offsetHeight;
    setSubmitButtonHeight(buttonHeight);

    const bottomRootMargin = Math.max(0, buttonHeight) + 16;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsButtonFixed(!entry.isIntersecting);
        });
      },
      {
        rootMargin: `0px 0px -${bottomRootMargin}px 0px`
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [isCheckoutModal, isComplianceCheckboxEnabled, isIosNativeApp, isMobile]);


  const elementsModePaymentOrSetup = useMemo(() => {
    const amountMinorUnits = toMinorUnits(getPriceTotal(plan, coupon), plan.currency);
    if (isTrial(plan, coupon) || canUpgradeExistingSubscription || amountMinorUnits === 0) {
      return { mode: 'setup' as const };
    }
    return { amount: amountMinorUnits, mode: 'payment' as const };
  }, [canUpgradeExistingSubscription, coupon, plan]);

  function logExpressCheckoutPurchaseButtonTapped(event) {
    logPurchaseButtonTapped({
      payment_method: getAnalyticsPaymentMethod(event.expressPaymentType),
      product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
      web_purchase_location: webProPurchaseLocation,
      ...(trigger && { upsell_trigger: trigger })
    });
  }

  useEffect(() => {
    async function setupStripe() {
      let stripe: Awaited<ReturnType<typeof loadStripe>>;
      try {
        stripe = await stripePromise;
      } catch (e) {
        logError(new Error('loadStripe failed', { cause: e }));
        logPurchaseFailed({ error_code: 'stripe_not_loaded', failure_type: 'loadStripe promise rejected' });
        return;
      }

      stripeRef.current = stripe;

      // loadStripe() can return null if the script fails to load or the key is invalid
      if (!stripe) {
        logPurchaseFailed({ error_code: 'stripe_not_loaded' });
        return;
      }

      // https://github.com/stripe/stripe-js/blob/master/types/stripe-js/elements-group.d.ts#L754
      const options: StripeElementsOptionsMode = {
        currency: currencyCode.toLowerCase(),
        locale: LANGUAGE_REGION_TO_STRIPE_LOCALE[languageRegionCode],
        ...(payPalSupportedCurrencies.includes(currencyCode) &&
          !isIosNativeApp && {
            customPaymentMethods: [{ id: process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD, options: { type: 'static' } }]
          }),
        // available payment method types are pushed down from Stripe // paymentMethodTypes: ['card'],
        fonts: [
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-regular-ee9d02127cfdb2aef717576a3881369ecc5e5f5c940bd2cc61a077c848c40b69.woff2)',
            weight: '400'
          },
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-medium-81496b49e1e345e2a7803ddfe8e98ddd695b66f385f43b66975fa53d8215abeb.woff2)',
            weight: '500'
          },
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-semibold-cc80f9e0c793ecfd7f632df9ce82f888e5f4a26cde3dee08c930492f15d6475f.woff2)',
            weight: '600'
          }
        ],
        // https://docs.stripe.com/elements/appearance-api
        appearance: {
          inputs: 'condensed',
          rules: {
            '.Input--invalid': {
              borderColor: COLOR_TEXT_ERROR
            },
            '.Label': {
              color: COLOR_TEXT_TERTIARY,
              // fontFeatureSettings: '"ss02"',
              fontSize: `${FONT_SIZE_200 / 16}rem`,
              letterSpacing: `${FONT_LETTERSPACING_200 / 16}rem`,
              lineHeight: String(FONT_LINEHEIGHT_DEFAULT)
            },
            '.Error': {
              fontSize: `${FONT_SIZE_100 / 16}rem`,
              fontWeight: String(FONT_WEIGHT_NORMAL)
            }
          },
          variables: {
            colorDanger: COLOR_TEXT_ERROR,
            fontFamily: `${FONT_FAMILY_PRIMARY}, sans-serif`
          }
        },
        setupFutureUsage: 'off_session',
        excludedPaymentMethodTypes: ['cashapp'],
        ...elementsModePaymentOrSetup
        // paymentMethodConfiguration uses the default search for "pmc_" in devtools network to find the session,
      } as const;

      let elements;
      try {
        elements = stripe.elements(options);
      } catch (e) {
        setErrors({ base: [] });
        logError(new Error('create elements returned an error', { cause: e }));
        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'create elements returned an error'
        });
      }
      elementsRef.current = elements;

      if (isChrome) {
        // https://docs.stripe.com/js/element/express_checkout_element
        const expressCheckoutOptions: StripeExpressCheckoutElementOptions = {
          ...(applePayConfig && { applePay: applePayConfig }),
          billingAddressRequired: true,
          layout: {
            overflow: 'never'
          },
          paymentMethods: {
            applePay: 'always', // Always show Apple Pay so it appears on Chrome and uses the QR code,
            link: 'never', // Hugh is testing,
            googlePay: 'always' // Stripe will hide Google Pay even when it is available and Stripe doesn't provide logging
          }
        };

        let expressCheckoutElement;
        try {
          expressCheckoutElement = elements.create('expressCheckout', expressCheckoutOptions);
        } catch (e) {
          logError(new Error('create express checkout element returned an error', { cause: e }));
          logPurchaseFailed({
            error_code: 'create express checkout element returned an error',
            failure_type: 'create express checkout element returned an error'
          });
          return;
        }

        // https://docs.stripe.com/js/elements_object/express_checkout_element_click_event
        expressCheckoutElement.on('click', event => {
          logExpressCheckoutPurchaseButtonTapped(event);
          event.resolve();
        });

        // https://docs.stripe.com/js/elements_object/express_checkout_element_confirm_event
        expressCheckoutElement.on('confirm', async event => {
          const recaptcha = await executeRecaptcha('userPaymentAction');

          let confirmationTokenResponse;
          try {
            // https://docs.stripe.com/js/confirmation_tokens/create_confirmation_token
            confirmationTokenResponse = await stripe.createConfirmationToken({
              elements: elementsRef.current!,
              params: {
                payment_method_data: {
                  billing_details: {
                    // billing details come from the Google Pay or Apple Pay wallet automatically and we want to send them back to Stripe
                    name: event.billingDetails?.name,
                    email: event.billingDetails?.email,
                    phone: event.billingDetails?.phone,
                    address: event.billingDetails?.address
                  }
                },
                return_url: 'https://www.alltrails.com'
              }
            });
          } catch (e) {
            // this is a developer error so we don't want to show it to the user
            setErrors({ base: [] });

            logError(new Error('express checkout create confirmation threw an error', { cause: e }));

            logPurchaseFailed({
              error_code: (e as Error).message,
              failure_type: 'express checkout create confirmation threw an error'
            });

            return;
          }

          if (confirmationTokenResponse.error) {
            setErrors({ base: [] });
            logError(new Error('express checkout create confirmation token returned an error', { cause: confirmationTokenResponse.error }));
            return;
          }

          if (!confirmationTokenResponse.confirmationToken) {
            setErrors({ base: [] });
            logError(new Error('express checkout no confirmation token returned'));
            return;
          }

          try {
            await onPaymentToken({
              payment_method_type: getPaymentMethodTypeFromExpressCheckoutEvent(event.expressPaymentType),
              recaptcha,
              token: confirmationTokenResponse.confirmationToken.id,
              tokenization_source: 'stripe'
            });
          } catch (e) {
            logError(new Error('express checkout onPaymentToken failed', { cause: e }));
            logPurchaseFailed({
              ...(e instanceof ApiError && { error_code: e.data.base[0] }),
              payment_method: getAnalyticsPaymentMethod(event.expressPaymentType),
              failure_type: 'express checkout onPaymentToken failed'
            });

            if (e instanceof ApiError) {
              setErrors(e.data);
            } else {
              setErrors({ base: [] });
            }

            setSubmitting(false);
          }
        });

        expressCheckoutElement.on('loaderror', event => {
          console.error('Express Checkout failed to load:', event.error);
        });

        expressCheckoutElementRef.current = expressCheckoutElement;
        if (expressMountRef.current) expressCheckoutElement.mount(expressMountRef.current);
      }

      // https://docs.stripe.com/js/elements_object/create_payment_element
      const paymentElement = elements.create('payment', {
        ...(applePayConfig && { applePay: applePayConfig }),
        fields: { billingDetails: { address: 'never' } },
        layout: {
          type: 'accordion',
          defaultCollapsed: false,
          ...(!isChrome && { radios: 'always' })
        },
        ...((isIosNativeApp || (isMobile && browserName?.includes('Safari'))) && {
          paymentMethodOrder: ['apple_pay'] // make sure apple pay is first based upon our A/B experiments
        }),
        terms: {
          applePay: 'never',
          auBecsDebit: 'never',
          bancontact: 'never',
          card: 'never',
          cashapp: 'never',
          googlePay: 'never',
          ideal: 'never',
          paypal: 'never',
          sepaDebit: 'never',
          sofort: 'never',
          usBankAccount: 'never'
        },
        wallets: {
          link: 'never' // Hugh is testing
        }
      });

      paymentElement.on('change', event => {
        setSelectedPaymentType(event.value.type);
      });

      paymentElementRef.current = paymentElement;
      if (paymentMountRef.current) paymentElement.mount(paymentMountRef.current);
    }

    setupStripe();

    return () => {
      expressCheckoutElementRef.current?.unmount();
      expressCheckoutElementRef.current = null;
      paymentElementRef.current?.unmount();
      paymentElementRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- plan, coupon, currencyCode and canUpgradeExistingSubscription are intentionally omitted; `elements.update` runs in the effect below when currency, mode, or amount should change (canUpgradeExistingSubscription also affects applePayConfig when applicable)
  }, [
    applePayConfig,
    browserName,
    isChrome,
    isIosNativeApp,
    isMobile,
    languageRegionCode,
    onPaymentToken,
    purchaseTier,
    trigger,
    webProPurchaseLocation
  ]);

  useEffect(() => {
    if (elementsRef.current) {
      try {
        //https://docs.stripe.com/js/elements_object/update
        elementsRef.current.update({
          currency: currencyCode.toLowerCase(),
          ...elementsModePaymentOrSetup
        });
      } catch (e) {
        logError(new Error('update elements returned an error', { cause: e }));
        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'update elements returned an error'
        });
      }
    }
  }, [currencyCode, elementsModePaymentOrSetup]);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();

    if (submitting) {
      return;
    }

    setErrors(undefined);

    if (!elementsRef.current) {
      logPurchaseFailed({ error_code: 'stripe elements not loaded', failure_type: 'stripe elements not loaded' });
      logError('stripe elements not loaded');
      setSubmitting(false);
      return;
    }

    const submitResult = await elementsRef.current.submit();

    // Note: selectedPaymentMethod is never returned if there is an error
    const { selectedPaymentMethod } = submitResult;

    if (!isGiftCard) {
      if (selectedPaymentType === 'card') {
        const hasFieldErrors = fullName.length < 1 || !hasValidLastName(fullName) || (showPostalCode && !isValidPostalCode(postalCode));

        // only the first error is returned in Stripe's submitResult
        if (hasFieldErrors || submitResult.error) {
          setClientValidationErrors(CARD_VALIDATION_NAMES.NAME, fullName);
          if (showPostalCode) setClientValidationErrors(CARD_VALIDATION_NAMES.POSTAL_CODE, postalCode);
          return;
        }
      }

      // validation errors are automatically displayed under the credit card fields
      if (submitResult.error && submitResult.error.message) {
        logPurchaseFailed({ error_code: submitResult.error.message, failure_type: 'submit returned an error' });
        setErrors({ base: [submitResult.error.message] });
        return;
      }
    }

    const stripe = stripeRef.current;

    if (!stripe) {
      logPurchaseFailed({ error_code: 'stripe not loaded', failure_type: 'stripe not loaded' });
      return;
    }

    try {
      setSubmitting(true);

      if (submitResult.error) {
        logPurchaseFailed({ error_code: submitResult.error.message, failure_type: 'submit returned an error' });
        return;
      }

      if (!selectedPaymentMethod) {
        logError(new Error('no payment method selected'));
        logPurchaseFailed({ error_code: 'no payment method selected' });
        return;
      }

      logPurchaseButtonTapped({
        payment_method: getAnalyticsPaymentMethod(selectedPaymentMethod),
        product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
        purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
        web_purchase_location: webProPurchaseLocation,
        ...(trigger && { upsell_trigger: trigger })
      });

      if (selectedPaymentMethod === process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD) {
        recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);

        const payPal = recurly.PayPal({
          display: {
            displayName: purchaseTier === 'peak' ? 'AllTrails Peak' : 'AllTrails Plus',
            amount: String(getPriceTotal(plan, coupon))
          }
        });

        payPal.on('error', (error: RecurlyError) => {
          logError(error);
          logPurchaseFailed({ error_code: error.message, payment_method: 'paypal' });
        });

        payPal.on('token', async token => {
          try {
            setSubmitting(true);

            const recaptcha = await executeRecaptcha('userPaymentAction');

            await onPaymentToken({
              payment_method_type: 'paypal',
              recaptcha,
              token: token.id,
              tokenization_source: 'recurly_js'
            });
          } catch (e) {
            logPurchaseFailed({
              ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
              payment_method: 'paypal'
            });

            if (e instanceof ApiError) {
              setErrors(e.data);
            } else {
              setErrors({ base: [] });
            }
            setSubmitting(false);
          }
        });

        payPal.start();
        return;
      }

      const recaptcha = await executeRecaptcha('userPaymentAction');

      if (isGiftCard) {
        await onSubmitTokenizedCardForm({ recaptcha });
        setSubmitting(false);
        return;
      }

      let confirmationTokenResponse;
      try {
        // https://docs.stripe.com/js/confirmation_tokens/create_confirmation_token
        confirmationTokenResponse = await stripe.createConfirmationToken({
          elements: elementsRef.current,
          params: {
            payment_method_data: {
              billing_details: {
                address: {
                  city: null,
                  country: billingCountry,
                  line1: null,
                  state: null,
                  postal_code: postalCode ? postalCode : null
                }
              }
            },
            return_url: 'https://www.alltrails.com'
          }
        });
      } catch (e) {
        // this is a developer error so we don't want to show it to the user
        setErrors({ base: [] });

        logError(new Error('create confirmation token returned an error', { cause: e }));

        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'create confirmation token returned an error'
        });

        return;
      }

      if (confirmationTokenResponse.error) {
        logPurchaseFailed({
          error_code: confirmationTokenResponse.error.message,
          failure_type: 'create confirmation token returned an error'
        });
        return;
      }

      if (!confirmationTokenResponse.confirmationToken) {
        logPurchaseFailed({
          failure_type: 'create confirmation token returned no confirmation token'
        });
        return;
      }

      const payment_method_type = getPaymentMethodTypeForToken(selectedPaymentMethod);

      await onPaymentToken({
        payment_method_type,
        recaptcha,
        token: confirmationTokenResponse.confirmationToken.id,
        tokenization_source: 'stripe'
      });
    } catch (e) {
      if (e instanceof ApiError) setErrors(e.data);
      logPurchaseFailed({
        ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
        failure_type: 'submit returned an error'
      });
    } finally {
      setSubmitting(false);
    }
  }

  const isFinePrintLeftAligned = billingCountry === 'US' && !isCheckoutModal && !isMobile && !isComplianceCheckboxEnabled;

  return (
    <form
      className={classNames(styles.content, className, {
        [styles.complianceCheckboxEnabled]: isComplianceCheckboxEnabled,
        [styles.iosWebCheckoutOptimizationEnabled]: isIosNativeApp,
        [styles.buttonToggleTreatmentVariant]: isIosNativeApp,
        [styles.hierarchyCardCtaAndTimelineVariant]: isIosNativeApp
      })}
      style={{
        paddingBottom: isMobile && isComplianceCheckboxEnabled && isButtonFixed ? `${submitButtonHeight}px` : undefined
      }}
      onSubmit={handleSubmit}
    >
      <div className={styles.columnContainer}>
        <div className={styles.tryArea}>
          <h1
            className={classNames(styles.heading, headingClassName, { [styles.complianceHeading]: isComplianceCheckboxEnabled })}
            id={ariaLabelledBy}
          >
            {heading}
          </h1>
          {isIosNativeApp && (
            <div className={styles.timeline}>
              <TrialReminder currencyCode={currencyCode} cost={plan.priceTotal} tier={purchaseTier} selected={true} />
            </div>
          )}
          {subheading && (
            <div className={styles.subheading}>
              <Typography variant="text100">{subheading}</Typography>
            </div>
          )}
          {features}
        </div>
        <div className={styles.billedCopyright}>
          <div className={styles.billedArea}>
            <div className={styles.headings}>
              <div className={styles.billed}>
                <Typography component="div" variant="text200bold">
                  {billedHeading || <FormattedMessage defaultMessage="Billed today" />}
                </Typography>
                <Typography component="div" variant="text200">
                  {billedToday}
                </Typography>
              </div>
              <div className={styles.hr} />
              {billedSubheading && (
                <div className={classNames(styles.after, billedSubheadingClassName)}>
                  <Typography component="div" variant="text200bold">
                    {billedSubheading}
                  </Typography>
                  <Typography component="div" variant="text200">
                    {billedSubheadingRight}
                  </Typography>
                </div>
              )}
            </div>
            {setCoupon && setPlanToAnnual && !isIosNativeApp && (
              <div>
                <CouponCode
                  couponError={couponError}
                  coupon={coupon}
                  purchaseTier={purchaseTier}
                  setCoupon={setCoupon}
                  setPlanToAnnual={setPlanToAnnual}
                />
              </div>
            )}
          </div>
          {finePrint && isFinePrintLeftAligned && <div className={classNames(styles.fine, styles.leftAlign)}>{finePrint}</div>}
          {isComplianceCheckboxEnabled && (
            <ComplianceCheckbox
              finePrint={finePrint}
              checked={isComplianceChecked}
              onChange={checked => {
                setIsComplianceChecked(checked);
                setComplianceError(false);
              }}
              error={complianceError}
            />
          )}
          <div
            className={classNames(styles.copyright, {
              [styles.leftAlign]: isFinePrintLeftAligned,
              [styles.complianceVariant]: isComplianceCheckboxEnabled
            })}
          >
            {getCopyrightText({ intl })}
            <br />
            <div>
              <Link className={styles.link} href="/privacy" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Privacy Policy" />
              </Link>{' '}
              |{' '}
              <Link className={styles.link} href="/terms" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Terms of Service" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.columnContainer}>
        <div className={classNames(styles.form, { [styles.complianceEnabled]: isComplianceCheckboxEnabled })}>
          {isChrome && <div ref={expressMountRef} />}

          {isChrome && (
            <SectionDivider>
              <FormattedMessage defaultMessage="Or enter payment details" />
            </SectionDivider>
          )}

          <div className={classNames(tokenizedStyles.formInputContainer, { [styles.complianceVariant]: isComplianceCheckboxEnabled })}>
            <div ref={paymentMountRef} />
            {selectedPaymentType === 'card' && (
              <>
                <div className={tokenizedStyles.namesContainer}>
                  <TextInput
                    autoComplete="cc-name"
                    className={classNames(tokenizedStyles.formInput, { [tokenizedStyles.invalidInput]: !!nameError })}
                    errorText={nameError}
                    labelText={<FormattedMessage defaultMessage="Name on card" />}
                    labelTextColor="tertiary"
                    name="name"
                    onChange={(value: string) => {
                      setFullName(value);
                      setClientValidationErrors(CARD_VALIDATION_NAMES.NAME, value);
                    }}
                    placeholder={intl.formatMessage({ defaultMessage: 'Name on card' })}
                    value={fullName}
                  />
                </div>
                {showPostalCode && (
                  <div>
                    <TextInput
                      autoComplete="postal-code"
                      className={classNames(tokenizedStyles.formInput, { [tokenizedStyles.invalidInput]: !!postalCodeError })}
                      errorText={postalCodeError}
                      labelText={<FormattedMessage defaultMessage="Postal code" />}
                      labelTextColor="tertiary"
                      name="postal code"
                      onChange={(value: string) => {
                        setPostalCode(value);
                        setClientValidationErrors(CARD_VALIDATION_NAMES.POSTAL_CODE, value);
                      }}
                      placeholder={intl.formatMessage({ defaultMessage: 'Postal code' })}
                      value={postalCode}
                    />
                  </div>
                )}
                {showBillingCountry && (
                  <BillingCountrySelector
                    billingCountryCode={billingCountry}
                    labelText={intl.formatMessage({ defaultMessage: 'Billing country' })}
                    onChangeBillingCountry={async e => {
                      const response = await getAvailablePlans(e as CountryCode, purchaseTier);
                      onBillingCountryChange(e as CountryCode, response);
                    }}
                  />
                )}
              </>
            )}
          </div>
          {errors && <ErrorText errors={errors} />}
        </div>
        {isCheckoutModal && (
          <StripeCheckoutModalFooter
            coupon={coupon}
            isRadioUiEnabled={isRadioUiEnabled}
            onApplePayClick={() => void handleSubmit()}
            selectedPaymentType={selectedPaymentType}
            submitting={submitting}
          />
        )}
        {!isCheckoutModal && (
          <div ref={submitButtonContainerRef}>
            <div className={classNames(styles.buttonContainer, { [styles.fixedButtonContainer]: isButtonFixed })}>
              {selectedPaymentType !== 'apple_pay' && selectedPaymentType !== process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD && (
                <Button className={styles.button} loading={submitting} text={buttonText} type="submit" variant="primary" />
              )}
              {selectedPaymentType === process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD && <StripePayPalButton submitting={submitting} />}
              {selectedPaymentType === 'apple_pay' && <StripeApplePayButton onClick={() => void handleSubmit()} />}
              {isIosNativeApp && (
                <TogglePaymentButton
                  selectedPaymentType={
                    selectedPaymentType === 'apple_pay'
                      ? PaymentOptionValues.Apple
                      : selectedPaymentType === 'card'
                        ? PaymentOptionValues.CreditCard
                        : undefined
                  }
                  setSelectedPaymentType={value => {
                    setSelectedPaymentType(value === PaymentOptionValues.Apple ? 'apple_pay' : 'card');
                  }}
                />
              )}
            </div>
          </div>
        )}
        {finePrint && !isFinePrintLeftAligned && !isComplianceCheckboxEnabled && <div className={styles.fine}>{finePrint}</div>}
        {promoText && <div className={styles.promoText}>{promoText}</div>}
      </div>
    </form>
  );
}

export default StripeCheckoutBase;
